/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import user from 'js/lib/user';

import type { ProgramType } from 'bundles/audit/constants';
import { PROGRAM_TYPES } from 'bundles/audit/constants';
import straightToCheckoutEnroll from 'bundles/audit/utils/straightToCheckoutEnroll';
import type { straightToCheckoutEnrollParameters } from 'bundles/audit/utils/straightToCheckoutEnroll';
import EnrollModal from 'bundles/enroll/components/EnrollModal';
import { useCourseEnrollModalData } from 'bundles/enroll/data/usePageData';
import { useNaptime } from 'bundles/naptimejs';
import GuidedCourseNextStepsV1 from 'bundles/naptimejs/resources/guidedCourseNextSteps.v1';
import S12nProductVariants from 'bundles/s12n-common/constants/s12nProductVariants';
import { useIsEnterpriseLearner } from 'bundles/unified-description-page-common/contexts/UserContext';

export type ProductPrice = { currencyCode: string; amount: number };

export type AuditUpgradeFlowDataContextType = {
  handleStartFreeTrialClick: () => void;
  productPrice?: ProductPrice;
  programType: ProgramType;
};

const getHandleStartFreeTrialClick = (straightToCheckoutEnrollParameters: straightToCheckoutEnrollParameters) => {
  return () => straightToCheckoutEnroll(straightToCheckoutEnrollParameters);
};

export const AuditUpgradeFlowDataContext = React.createContext<AuditUpgradeFlowDataContextType | null>(null);

export const useAuditUpgradeFlowData = () => useContext(AuditUpgradeFlowDataContext);

const styles = {
  enrollModalZIndexOverride: css`
    .c-modal-overlay {
      z-index: 5000;
    }
  `,
};

export const AuditUpgradeFlowDataContextProvider: React.LegacyFunctionComponentWithChildren<{
  isProductAndUserEligibleForAuditUpgradeFlow: boolean;
}> = ({ isProductAndUserEligibleForAuditUpgradeFlow, children }) => {
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const { isEnterpriseLearner, loadingIsEnterpriseLearner } = useIsEnterpriseLearner();
  const { course, s12n, s12nDerivatives, enrollmentAvailableChoices } = useCourseEnrollModalData();
  const { isMixAndMatch } = enrollmentAvailableChoices;

  const {
    data: { guidedCourseNextSteps },
    pending: loadingCourseNextSteps,
  } = useNaptime<{ guidedCourseNextSteps: GuidedCourseNextStepsV1[] }>(() => {
    if (!course?.slug) {
      return {};
    }

    return {
      guidedCourseNextSteps: GuidedCourseNextStepsV1.multiGet([`${user.get().id}~${course.slug}`], {
        fields: ['id', 'nextStep'],
      }),
    };
  });

  const nextStepWithCourseMetadata = guidedCourseNextSteps?.[0]?.nextStep;
  const hasCompletedCourse = nextStepWithCourseMetadata?.typeName === 'courseCompletedNextStep';

  let programType: ProgramType = PROGRAM_TYPES.specialization;
  if (s12n?.productVariant !== S12nProductVariants.NormalS12n) {
    programType = PROGRAM_TYPES.professionalCertificate;
  }
  if (isMixAndMatch) {
    programType = PROGRAM_TYPES.mixAndMatch;
  }

  const auditUpgradeFlowDataContextValue: AuditUpgradeFlowDataContextType = {
    handleStartFreeTrialClick: isMixAndMatch
      ? () => setShowEnrollModal(true)
      : getHandleStartFreeTrialClick({
          courseId: course.id,
          s12nId: s12n?.id,
          enrollmentAvailableChoices,
        }),
    programType,
    productPrice: s12nDerivatives?.catalogPrice ?? undefined,
  };

  const shouldShowAuditUpgradeFlowExperience =
    isProductAndUserEligibleForAuditUpgradeFlow &&
    !loadingIsEnterpriseLearner &&
    !isEnterpriseLearner &&
    !loadingCourseNextSteps &&
    !hasCompletedCourse;

  return (
    <AuditUpgradeFlowDataContext.Provider
      value={shouldShowAuditUpgradeFlowExperience ? auditUpgradeFlowDataContextValue : null}
    >
      {isMixAndMatch && showEnrollModal && (
        <div css={styles.enrollModalZIndexOverride}>
          <EnrollModal courseId={course.id} onClose={() => setShowEnrollModal(false)} />
        </div>
      )}
      {children}
    </AuditUpgradeFlowDataContext.Provider>
  );
};
